import React, { useState } from "react";

import { ApolloProvider } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import type { Container } from "inversify";
import { IntlProvider } from "react-intl";
import { Router } from "react-router";
import type { Theme, ToastContainerProps } from "react-toastify";
import { ToastContainer } from "react-toastify";

import { AcceptDriverTermsAndConditionsOverlay } from "@features/driverTermsAndConditions/components/AcceptDriverTermsAndConditionsOverlay/AcceptDriverTermsAndConditionsOverlay";

import { getApolloClient } from "./apolloClient/apolloClient";
import { FEATURE_TERMS_AND_CONDITIONS, GOOGLE_MAPS_API_KEY } from "./config.management";
import { stores } from "./container";
import { AuthenticationStoreProvider } from "./domain/authentication/AuthenticationStore.provider";
import { queryClient } from "./queryClient";
import { rootRoute } from "./routes";
import { apiTrpcClient } from "./trpc/api/apiTrpcClient";
import { apiTrpcReact } from "./trpc/api/apiTrpcReact";
import { freshTrpcClient } from "./trpc/fresh/freshTrpcClient";
import { freshTrpcReact } from "./trpc/fresh/freshTrpcReact";
import { GoogleApiProvider } from "@/utils/react-shared-components";

const TOAST_OPTIONS: ToastContainerProps = {
    theme: "light" as Theme,
};

const ErrorFallback = ({ error, componentStack }: any) => {
    return (
        <p>
            <pre>{JSON.stringify(error)}</pre>
            Stack:
            <pre>{componentStack}</pre>
        </p>
    );
};

const App = ({ container }: { container: Container }) => {
    const [error, setError] = useState({});
    const onError = (err: unknown, componentStack: string, eventId: string) => {
        setError({
            error: err,
            componentStack,
            eventId,
        });
    };

    return (
        <QueryClientProvider client={queryClient}>
            <freshTrpcReact.Provider client={freshTrpcClient} queryClient={queryClient}>
                <apiTrpcReact.Provider client={apiTrpcClient} queryClient={queryClient}>
                    <ApolloProvider client={getApolloClient()}>
                        <AuthenticationStoreProvider>
                            <GoogleApiProvider googleApiKey={GOOGLE_MAPS_API_KEY || ""}>
                                <ErrorBoundary
                                    onError={onError}
                                    fallback={(errorData) => (
                                        <ErrorFallback
                                            error={errorData.error}
                                            componentStack={errorData.componentStack}
                                        />
                                    )}
                                >
                                    <IntlProvider locale="en">
                                        <Router
                                            routes={rootRoute}
                                            history={container.get<History>(stores.history) as any}
                                        />
                                        <ToastContainer {...TOAST_OPTIONS} />
                                        {FEATURE_TERMS_AND_CONDITIONS && <AcceptDriverTermsAndConditionsOverlay />}
                                    </IntlProvider>
                                </ErrorBoundary>
                            </GoogleApiProvider>
                        </AuthenticationStoreProvider>
                    </ApolloProvider>
                </apiTrpcReact.Provider>
            </freshTrpcReact.Provider>
        </QueryClientProvider>
    );
};

export default App;
